/**
 * File utilities for chat file uploads
 * Handles validation, type checking, and file metadata
 */

// Supported MIME types for file uploads
export const SUPPORTED_FILE_TYPES = {
  PDF: "application/pdf",
  PNG: "image/png",
  JPEG: "image/jpeg",
  WEBP: "image/webp",
  GIF: "image/gif",
  TEXT: "text/plain",
  CSV: "text/csv",
  DOC: "application/msword",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
} as const;

export const SUPPORTED_MIME_TYPES = Object.values(SUPPORTED_FILE_TYPES);

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  DEFAULT: 10 * 1024 * 1024, // 10MB
  IMAGE: 5 * 1024 * 1024, // 5MB
  PDF: 20 * 1024 * 1024, // 20MB
  DOCUMENT: 10 * 1024 * 1024, // 10MB
} as const;

// File type categories
export const FILE_CATEGORIES = {
  IMAGE: ["image/png", "image/jpeg", "image/webp", "image/gif"],
  DOCUMENT: [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  TEXT: ["text/plain", "text/csv"],
} as const;

/**
 * Validate if a file type is supported
 */
export function isSupportedFileType(mimeType: string): boolean {
  return SUPPORTED_MIME_TYPES.includes(mimeType as any);
}

/**
 * Get the file size limit for a given MIME type
 */
export function getFileSizeLimit(mimeType: string): number {
  if (FILE_CATEGORIES.IMAGE.includes(mimeType as (typeof FILE_CATEGORIES.IMAGE)[number])) {
    return FILE_SIZE_LIMITS.IMAGE;
  }
  if (FILE_CATEGORIES.DOCUMENT.includes(mimeType as (typeof FILE_CATEGORIES.DOCUMENT)[number])) {
    return FILE_SIZE_LIMITS.PDF;
  }
  if (FILE_CATEGORIES.TEXT.includes(mimeType as (typeof FILE_CATEGORIES.TEXT)[number])) {
    return FILE_SIZE_LIMITS.DEFAULT;
  }
  return FILE_SIZE_LIMITS.DEFAULT;
}

/**
 * Validate file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!isSupportedFileType(file.type)) {
    return {
      valid: false,
      error: `File type not supported. Supported types: ${SUPPORTED_MIME_TYPES.join(", ")}`,
    };
  }

  // Check file size
  const sizeLimit = getFileSizeLimit(file.type);
  if (file.size > sizeLimit) {
    const limitMB = Math.round(sizeLimit / (1024 * 1024));
    return {
      valid: false,
      error: `File size exceeds limit of ${limitMB}MB`,
    };
  }

  return { valid: true };
}

/**
 * Get file category from MIME type
 */
export function getFileCategory(mimeType: string): keyof typeof FILE_CATEGORIES | "OTHER" {
  const categories = Object.entries(FILE_CATEGORIES) as Array<
    [keyof typeof FILE_CATEGORIES, readonly string[]]
  >;
  for (const [category, types] of categories) {
    if (types.includes(mimeType)) {
      return category;
    }
  }
  return "OTHER";
}

/**
 * Check if file type supports text extraction
 */
export function supportsTextExtraction(mimeType: string): boolean {
  return (
    FILE_CATEGORIES.DOCUMENT.includes(mimeType as (typeof FILE_CATEGORIES.DOCUMENT)[number]) ||
    FILE_CATEGORIES.TEXT.includes(mimeType as (typeof FILE_CATEGORIES.TEXT)[number])
  );
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
}

/**
 * Get MIME type from file extension
 */
export function getMimeTypeFromExtension(extension: string): string | null {
  const ext = extension.toLowerCase();
  const mimeMap: Record<string, string> = {
    pdf: SUPPORTED_FILE_TYPES.PDF,
    png: SUPPORTED_FILE_TYPES.PNG,
    jpg: SUPPORTED_FILE_TYPES.JPEG,
    jpeg: SUPPORTED_FILE_TYPES.JPEG,
    webp: SUPPORTED_FILE_TYPES.WEBP,
    gif: SUPPORTED_FILE_TYPES.GIF,
    txt: SUPPORTED_FILE_TYPES.TEXT,
    csv: SUPPORTED_FILE_TYPES.CSV,
    doc: SUPPORTED_FILE_TYPES.DOC,
    docx: SUPPORTED_FILE_TYPES.DOCX,
  };
  return mimeMap[ext] || null;
}

/**
 * Generate a unique file path for storage
 */
export function generateFilePath(
  userId: string,
  chatId: string,
  fileId: string,
  filename: string
): string {
  const extension = getFileExtension(filename);
  return `chat-files/${userId}/${chatId}/${fileId}${extension ? "." + extension : ""}`;
}
